import { useEffect, useState } from "react";
import { FileSpreadsheet, FileText, FileDown, Play, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SearchSelect from "@/components/SearchSelect";
import { PageHeader, EmptyState, Pager, Spinner, StatusBadge } from "@/components/common";
import { api, cleanParams, downloadFile, formatErr } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtMoney } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const FILTERS = {
  "client-register": ["client_id", "vendor_id", "item_id", "invoice_from", "invoice_to", "entry_from", "entry_to"],
  "vat-summary": ["client_id", "vendor_id", "item_id", "invoice_from", "invoice_to"],
  "vendor-register": ["vendor_id", "client_id", "item_id", "invoice_from", "invoice_to"],
  "invoice-search": ["client_id", "vendor_id", "item_id", "invoice_number", "vat_number", "q"],
  "missing-attachments": ["client_id", "vendor_id", "item_id"],
  "sales-register": ["client_id", "date_from", "date_to"],
  "vat-position": ["client_id", "date_from", "date_to"],
  duplicates: ["client_id"],
  "audit-review": ["client_id", "auditor_id", "review_status", "date_from", "date_to"],
  "staff-performance": ["staff_id", "date_from", "date_to"],
  "user-activity": ["user_id", "action", "date_from", "date_to"],
  "assignment-history": ["client_id", "staff_id", "date_from", "date_to"],
};
const LABELS = { client_id: "Client", vendor_id: "Vendor", item_id: "Product / item", staff_id: "Staff", auditor_id: "Auditor", user_id: "User", review_status: "Review status",
  invoice_from: "Invoice date from", invoice_to: "Invoice date to", entry_from: "Entry date from", entry_to: "Entry date to", date_from: "From", date_to: "To",
  invoice_number: "Invoice number", vat_number: "Vendor VAT no.", q: "Phrase", action: "Activity type" };
const ACTIONS = [["auth", "Sign in / out"], ["bill", "Bill actions"], ["attachment", "Attachments"], ["review", "Reviews"], ["client", "Clients"], ["user", "Users"], ["vendor", "Vendors"], ["item", "Items"], ["report", "Exports"], ["settings", "Settings"]];

const BILL_REPORTS = ["client-register", "vendor-register", "invoice-search", "missing-attachments"];
const SEARCHABLE = ["client_id", "vendor_id", "item_id", "staff_id", "auditor_id", "user_id"];

const cell = (v, t) => {
  if (v == null || v === "") return "—";
  if (t === "money") return <span className="num">{fmtMoney(v)}</span>;
  if (t === "date") return <span className="num text-xs">{fmtDate(v)}</span>;
  if (t === "datetime") return <span className="num text-xs">{fmtDateTime(v)}</span>;
  if (t === "status") return <StatusBadge status={v} />;
  if (t === "int") return <span className="num">{v}</span>;
  return v;
};

const Pick = ({ value, onChange, options, testid }) => (
  <Select value={value || "all"} onValueChange={onChange}>
    <SelectTrigger className="h-9" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent className="bg-white max-h-72"><SelectItem value="all">All</SelectItem>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export default function Reports() {
  const { user, settings } = useAuth();
  const [list, setList] = useState([]);
  const [rtype, setRtype] = useState("client-register");
  const [flt, setFlt] = useState({});
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState({ clients: [], vendors: [], items: [], staff: [], auditors: [] });
  const [askFmt, setAskFmt] = useState(null);
  const canExport = settings?.export_roles?.includes(user.role);

  useEffect(() => {
    api.get("/reports").then(({ data }) => setList(data));
    Promise.all([api.get("/clients"), api.get("/vendors", { params: { limit: 5000 } }), api.get("/items", { params: { limit: 5000 } })])
      .then(([c, v, it]) => setOpts((o) => ({ ...o, clients: c.data, vendors: v.data, items: it.data })));
    if (user.role === "super_admin") Promise.all([api.get("/users", { params: { role: "staff" } }), api.get("/users", { params: { role: "auditor" } })]).then(([s, a]) => setOpts((o) => ({ ...o, staff: s.data, auditors: a.data })));
  }, [user.role]);

  const run = async (p = 1) => {
    setBusy(true);
    setPage(p);
    try { const { data } = await api.get(`/reports/${rtype}`, { params: { ...cleanParams(flt), page: p, page_size: 50 } }); setRes(data); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  useEffect(() => { setRes(null); setFlt({}); }, [rtype]);

  const exp = async (fmt, withLinks = false) => {
    setAskFmt(null);
    try {
      await downloadFile(`/reports/${rtype}`, { ...cleanParams(flt), format: fmt, ...(withLinks ? { include_attachments: "1" } : {}) }, `${rtype}.${fmt}`);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error("Export failed"); }
  };
  const startExport = (fmt) => (BILL_REPORTS.includes(rtype) ? setAskFmt(fmt) : exp(fmt));

  const set = (k, v) => setFlt((p) => ({ ...p, [k]: v === "all" ? "" : v }));
  const allUsers = [...opts.staff, ...opts.auditors];
  const renderFilter = (k) => {
    const map = { client_id: opts.clients.map((c) => [c.id, `${c.company_name} · ${c.code}`]), vendor_id: opts.vendors.map((v) => [v.id, v.vat_number ? `${v.name} · ${v.vat_number}` : v.name]), item_id: opts.items.map((v) => [v.id, v.name]), staff_id: opts.staff.map((s) => [s.id, s.name]),
      auditor_id: opts.auditors.map((s) => [s.id, s.name]), user_id: allUsers.map((s) => [s.id, `${s.name} (${s.role})`]), action: ACTIONS,
      review_status: [["reviewed", "Reviewed"], ["query_raised", "Query raised"], ["resolved", "Resolved"]] };
    if (map[k]) {
      if ((k === "auditor_id" || k === "staff_id" || k === "user_id") && user.role !== "super_admin") return null;
      if (SEARCHABLE.includes(k)) return <SearchSelect value={flt[k] || ""} onChange={(v) => set(k, v)} options={map[k]} testid={`report-filter-${k}`} />;
      return <Pick value={flt[k]} onChange={(v) => set(k, v)} options={map[k]} testid={`report-filter-${k}`} />;
    }
    const date = k.includes("from") || k.includes("to");
    return <Input type={date ? "date" : "text"} value={flt[k] || ""} onChange={(e) => set(k, e.target.value)} className="h-9 num text-xs" data-testid={`report-filter-${k}`} />;
  };

  return (
    <div>
      <PageHeader eyebrow="Insights" title="Reports" subtitle="Filter, review and export registers. Amounts in SAR; timestamps in Asia/Riyadh." />
      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
        <nav className="panel p-2 h-fit fade-up" data-testid="report-list">
          {list.map((r) => (
            <button key={r.id} onClick={() => setRtype(r.id)} data-testid={`report-type-${r.id}`}
              className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 transition-colors ${rtype === r.id ? "bg-emerald-800 text-white" : "hover:bg-slate-100 text-slate-700"}`}>
              <BarChart3 className="w-3.5 h-3.5 shrink-0 opacity-70" />{r.title}
            </button>
          ))}
        </nav>
        <div className="space-y-4 min-w-0">
          <div className="panel p-4 fade-up">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {FILTERS[rtype].map((k) => { const el = renderFilter(k); return el && <div key={k}><div className="text-[11px] text-slate-500 mb-1">{LABELS[k]}</div>{el}</div>; })}
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button onClick={() => run(1)} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900" data-testid="report-run-button"><Play className="w-4 h-4 mr-1.5" />{busy ? "Running…" : "Run report"}</Button>
              {canExport && <>
                <Button variant="outline" onClick={() => startExport("xlsx")} data-testid="export-xlsx-button"><FileSpreadsheet className="w-4 h-4 mr-1.5 text-emerald-700" />XLSX</Button>
                <Button variant="outline" onClick={() => startExport("pdf")} data-testid="export-pdf-button"><FileText className="w-4 h-4 mr-1.5 text-red-600" />PDF</Button>
                <Button variant="outline" onClick={() => startExport("csv")} data-testid="export-csv-button"><FileDown className="w-4 h-4 mr-1.5" />CSV</Button>
              </>}
            </div>
          </div>
          <div className="panel overflow-hidden fade-up">
            {busy && !res ? <Spinner /> : !res ? <EmptyState title="Run the report to see results" subtitle="Leave filters empty to include all authorised records." testid="report-empty" /> : (
              <>
                <div className="px-4 py-3 border-b border-slate-200 flex justify-between items-center"><div className="font-display font-semibold" data-testid="report-title">{res.title}</div><span className="num text-xs text-slate-500" data-testid="report-row-count">{res.total} rows</span></div>
                <div className="overflow-x-auto">
                  {!res.rows.length ? <EmptyState /> : (
                    <table className="w-full" data-testid="report-table">
                      <thead><tr>{res.columns.map((c) => <th key={c.key} className={`th ${c.type === "money" ? "text-right" : ""}`}>{c.label}</th>)}</tr></thead>
                      <tbody>
                        {res.rows.map((r, i) => <tr key={i} className="row-hover">{res.columns.map((c) => <td key={c.key} className={`td text-sm ${c.type === "money" ? "text-right" : ""}`}>{cell(r[c.key], c.type)}</td>)}</tr>)}
                        {Object.keys(res.totals).length > 0 && (
                          <tr className="bg-emerald-50/70 font-semibold" data-testid="report-totals-row">
                            {res.columns.map((c, i) => <td key={c.key} className={`td ${c.type === "money" ? "text-right" : ""}`}>{i === 0 ? "TOTAL (SAR)" : c.key in res.totals ? cell(res.totals[c.key], c.type) : ""}</td>)}
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
                {res.total > 50 && <Pager page={page} pageSize={50} total={res.total} onPage={run} />}
              </>
            )}
          </div>
        </div>
      </div>
      <Dialog open={!!askFmt} onOpenChange={(o) => !o && setAskFmt(null)}>
        <DialogContent className="bg-white" data-testid="export-options-dialog">
          <DialogHeader>
            <DialogTitle>Export options · {askFmt?.toUpperCase()}</DialogTitle>
            <DialogDescription>Include bill attachment links as reference columns? Each link is a public CDN URL — anyone holding the file can open the images.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Button onClick={() => exp(askFmt, true)} className="h-auto py-3 flex-col items-start bg-emerald-800 hover:bg-emerald-900" data-testid="export-with-links-button">
              <span className="font-semibold">Include attachment links</span><span className="text-[11px] font-normal opacity-80">Attachment 1, 2… columns with public URLs</span>
            </Button>
            <Button variant="outline" onClick={() => exp(askFmt, false)} className="h-auto py-3 flex-col items-start" data-testid="export-without-links-button">
              <span className="font-semibold">Data only</span><span className="text-[11px] font-normal text-slate-500">No attachment columns</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
